import { useCallback, useId, useMemo, useState } from 'react';
import {
  Button,
  Box,
  Radio,
  Icon,
  Modal,
  Title,
  Label,
  Spinner,
} from '@nimbus-ds/components';
import { ExclamationCircleIcon, ExternalLinkIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import {
  ThemesConfigResponseDto,
  UpdateContentPagesRequestDto,
} from '@tiendanube/common';
import { LanguagesType } from 'App/i18n';
import { Stack } from 'commons/components';
import { useForm } from 'commons/hooks';
import { LIGHTSPEED_THEMES } from 'domains/Online/ContentPages/pages/constants';
import { DesignType } from 'domains/Online/ContentPages/pages/types';
import { validationSchema } from 'domains/Online/ContentPages/validationSchema';
import useTranslationOnline from 'domains/Online/useTranslationOnline';

const EMPTY_CONTENT = '<span />';

interface DesignEditorLaunchModalProps {
  language: LanguagesType;
  initialValues: UpdateContentPagesRequestDto;
  isSubmitting: boolean;
  themesConfig: ThemesConfigResponseDto;
  onSubmit: (
    data: UpdateContentPagesRequestDto,
    designType: DesignType,
  ) => void;
  onClose: () => void;
}

export default function DesignEditorLaunchModal({
  language,
  initialValues,
  isSubmitting,
  themesConfig,
  onSubmit,
  onClose,
}: Readonly<DesignEditorLaunchModalProps>) {
  const t = useTranslationOnline(
    'contentPages.formPage.designEditorLaunchModal',
  );

  const initialValuesWithContent = useMemo(
    () => ({
      ...initialValues,
      content: {
        ...initialValues.content,
        [language]: initialValues.content[language] || EMPTY_CONTENT,
      },
    }),
    [initialValues, language],
  );

  const form = useForm({
    initialValues: initialValuesWithContent,
    validationSchema: validationSchema(language),
    onSubmit: async (data) => {
      onSubmit(data, selectedDesign);
    },
  });

  const currentTheme = themesConfig?.currentTheme;
  const draftTheme = themesConfig?.draft;

  const isLightspeedThemeCurrent =
    !!currentTheme && LIGHTSPEED_THEMES.includes(currentTheme.code);
  const isLightspeedThemeDraft =
    !!draftTheme && LIGHTSPEED_THEMES.includes(draftTheme.code);

  const [selectedDesign, setSelectedDesign] = useState<DesignType>(
    isLightspeedThemeCurrent
      ? 'current'
      : isLightspeedThemeDraft
      ? 'draft'
      : 'current',
  );

  const handleChangeTitle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      form.setFieldValue('title', {
        ...form.values.title,
        [language]: value,
      });
    },
    [form, language],
  );

  const handleSubmitClick = useCallback(() => {
    form.handleOnSubmit();
  }, [form]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const mode = initialValues?.id ? 'edit' : 'create';
  const shouldDisplayThemeOptions =
    isLightspeedThemeCurrent || isLightspeedThemeDraft;

  const titleValue = form.values?.title?.[language] ?? '';
  const hasTitleErrorForLang = !!form.errors[`title.${language}`];
  const appearanceTitle = hasTitleErrorForLang ? 'danger' : undefined;
  const helpTextTitle = hasTitleErrorForLang
    ? t(`${form.errors[`title.${language}`]}`)
    : undefined;
  const designTypeLabelId = useId();

  return (
    <Modal open onDismiss={handleClose}>
      <Modal.Header>
        <Title as="h4">
          {mode === 'create' ? t('title.create') : t('title.edit')}
        </Title>
      </Modal.Header>
      <Modal.Body padding="none">
        <Stack column spacing="base" align="stretch">
          {mode === 'create' && (
            <Stack.Item>
              <FormField.Input
                name="title"
                type="text"
                label={t('titleField.label')}
                placeholder={t('titleField.placeholder')}
                value={titleValue}
                onChange={handleChangeTitle}
                appearance={appearanceTitle}
                showHelpText={!!helpTextTitle}
                helpIcon={ExclamationCircleIcon}
                helpText={helpTextTitle}
                required
              />
            </Stack.Item>
          )}
          {shouldDisplayThemeOptions && (
            <Stack.Item>
              <Stack column spacing="tight" align="flex-start">
                <Stack.Item>
                  <Label id={designTypeLabelId}>{t('designType.label')}</Label>
                </Stack.Item>
                <Stack.Item>
                  <Box role="radiogroup" aria-labelledby={designTypeLabelId}>
                    <Stack column spacing="tight" align="flex-start">
                      <Radio
                        id="design-type-current"
                        name="designType"
                        label={`${t('designType.current')} (${
                          isLightspeedThemeCurrent
                            ? currentTheme.name
                            : t('designType.notCustomizableTheme', {
                                theme: currentTheme.name,
                              })
                        })`}
                        checked={selectedDesign === 'current'}
                        disabled={!isLightspeedThemeCurrent}
                        onChange={() => setSelectedDesign('current')}
                        value="current"
                      />

                      {draftTheme && (
                        <Radio
                          id="design-type-draft"
                          name="designType"
                          label={`${t('designType.draft')} (${
                            isLightspeedThemeDraft
                              ? draftTheme.name
                              : t('designType.notCustomizableTheme', {
                                  theme: draftTheme.name,
                                })
                          })`}
                          checked={selectedDesign === 'draft'}
                          disabled={!isLightspeedThemeDraft}
                          onChange={() => setSelectedDesign('draft')}
                          value="draft"
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          )}

          <Modal.Footer>
            <Box display="flex" justifyContent="space-between" gap="2">
              <Button appearance="neutral" onClick={handleClose}>
                {t('buttons.back')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleSubmitClick}
                type="submit"
                disabled={!titleValue || hasTitleErrorForLang || isSubmitting}
              >
                {isSubmitting && <Spinner color="currentColor" size="small" />}
                {mode === 'create' ? t('buttons.create') : t('buttons.edit')}
                <Icon
                  color="currentColor"
                  source={<ExternalLinkIcon size={14} />}
                />
              </Button>
            </Box>
          </Modal.Footer>
        </Stack>
      </Modal.Body>
    </Modal>
  );
}
